import React from 'react';
import { cx } from '@milanote/roma/utils/cx';
import { Modal, Dialog, DialogContent, ModalOverlay } from '@milanote/roma';

import Icon from '../../components/icons/Icon';
import { useEnsurePasswordManagersInteraction } from './hooks/useEnsurePasswordManagersInteraction';

import './RomaAuthModal.scss';

type RomaAuthModalHeaderProps = {
    title: string;
    subtitle?: string;
    className?: string;
};

type RomaAuthModalProps = {
    className?: string;
    overlay?: boolean;
    close: (() => void) | null;
};

type RomaAuthModalComponent = React.FC<RomaAuthModalProps> & {
    Header: React.FC<RomaAuthModalHeaderProps>;
};

const RomaAuthModalHeader: React.FC<RomaAuthModalHeaderProps> = ({ title, subtitle, className }) => {
    return (
        <div className={cx('flex flex-col items-center gap-3xl', className)}>
            <span className="shrink-0 text-primary">
                <Icon name="milanote-logo-medium" />
            </span>
            <div className="flex flex-col items-center gap-md w-full">
                <h1 className="text-center text-display-3xl font-serif text-primary">{title}</h1>
                {subtitle && <p className="text-center text-display-xs text-fg-tertiary font-normal">{subtitle}</p>}
            </div>
        </div>
    );
};

export const RomaAuthSheetContent: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
    children,
    className,
}) => {
    return <div className={cx('RomaAuthSheetContent w-full p-xl', className)}>{children}</div>;
};

const RomaAuthModal: RomaAuthModalComponent = ({ children, className, overlay, close }) => {
    useEnsurePasswordManagersInteraction();

    const backgroundStyle = overlay ? 'dim' : 'solid';
    const transitionStyle = overlay ? 'fade' : 'none';
    const isDismissable = Boolean(close);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen && close) {
            close();
        }
    };

    return (
        <ModalOverlay
            backgroundStyle={backgroundStyle}
            isOpen={true}
            transitionStyle={transitionStyle}
            onOpenChange={onOpenChange}
            isDismissable={isDismissable}
            className="RomaAuthModalOverlay"
        >
            <Modal className="max-w-110">
                <Dialog>
                    <DialogContent closeButton={isDismissable} className={cx('RomaAuthModal', 'max-w-110', className)}>
                        {children}
                    </DialogContent>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};

RomaAuthModal.Header = RomaAuthModalHeader;

export default RomaAuthModal;
